import { useState } from 'react';
import { Box, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import LockIcon from '@mui/icons-material/Lock';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import StorefrontIcon from '@mui/icons-material/Storefront';
import StripeCard from './StripeCard';
import StripeWallets from './StripeWallets';

// Opciones de método de pago. La tarjeta se registra ahora y se cobra al aceptar;
// SPEI/OXXO generan una referencia (CLABE/ficha) cuando el proveedor acepta la reserva.
const METODOS = [
  { key: 'card', label: 'Tarjeta', desc: 'Débito o crédito', icon: CreditCardIcon },
  { key: 'spei', label: 'SPEI', desc: 'Transferencia bancaria', icon: AccountBalanceIcon },
  { key: 'oxxo', label: 'OXXO', desc: 'Efectivo en tienda', icon: StorefrontIcon },
];

// motivo: por qué no se puede elegir (SPEI y OXXO piden anticipación y OXXO tiene un tope); entonces no responde y lo dice en vez de la
// descripción
const MetodoCard = ({ metodo, activo, motivo, onClick }) => {
  const Icono = metodo.icon;
  return (
    <Box
      onClick={motivo ? undefined : onClick}
      role="button"
      aria-disabled={motivo ? 'true' : undefined}
      sx={{
        flex: 1, minWidth: 120, cursor: motivo ? 'not-allowed' : 'pointer', px: 2, py: 1.75, borderRadius: '14px',
        border: '2px solid', borderColor: activo ? 'secondary.main' : 'divider',
        bgcolor: motivo ? 'grey.50' : activo ? ((t) => alpha(t.palette.secondary.main, 0.05)) : 'white', transition: 'all .15s',
        display: 'flex', flexDirection: 'column', gap: 0.5,
        '&:hover': { borderColor: activo ? 'secondary.main' : motivo ? 'divider' : 'grey.400' },
      }}
    >
      <Icono sx={{ fontSize: 22, color: motivo ? 'action.disabled' : activo ? 'secondary.main' : 'text.secondary' }} />
      <Typography sx={{ fontWeight: 800, color: motivo ? 'text.secondary' : 'primary.main', fontSize: 14 }}>{metodo.label}</Typography>
      <Typography sx={{ fontSize: 11, color: motivo ? 'warning.oscuro' : 'text.secondary' }}>{motivo || metodo.desc}</Typography>
    </Box>
  );
};

// Método de pago (Stripe). Tarjeta → campo seguro (se cobra al aceptar). SPEI/OXXO →
// referencia generada al aceptar; el cliente la paga y se confirma automáticamente.
// Apple/Google Pay (wallet) → botón exprés arriba: autoriza y guarda la tarjeta, igual
// que el campo de tarjeta, y crea la reserva en un solo paso (F4B).
// noDisponibles: { spei, oxxo } con el motivo de los que no se pueden elegir para esta reserva ('' o nada si sí)
const BookingPayment = ({
  paymentMethod = 'card', setPaymentMethod, stripeCardRef,
  onWalletPago, validarPago, onWalletError, noDisponibles = {},
}) => {
  // El botón de wallet solo se muestra si el navegador/dispositivo ofrece Apple/Google Pay.
  const [walletDisponible, setWalletDisponible] = useState(false);

  return (
    <Box sx={{ p: 4, borderRadius: '24px', bgcolor: 'white', border: 1, borderColor: 'sutil' }}>
      <Typography variant="h6" fontWeight="900" color="primary.main" sx={{ mb: 1 }}>
        Método de pago
      </Typography>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <LockIcon sx={{ fontSize: 14, color: 'success.oscuro' }} />
        <Typography variant="caption" color="success.oscuro" fontWeight={600}>
          Pago seguro con Stripe
        </Typography>
      </Box>

      {/* Pago exprés con wallet (Apple/Google Pay). Se monta siempre para poder detectar
          disponibilidad, pero solo ocupa espacio si hay alguna wallet. */}
      {onWalletPago && (
        <Box sx={{ mb: walletDisponible ? 3 : 0 }}>
          <StripeWallets
            validar={validarPago}
            onPago={onWalletPago}
            onError={onWalletError}
            onDisponible={setWalletDisponible}
          />
          {walletDisponible && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 3 }}>
              <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
              <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700 }}>
                o paga con
              </Typography>
              <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
            </Box>
          )}
        </Box>
      )}

      {/* Selector de método */}
      <Box sx={{ display: 'flex', gap: 1.5, mb: 3, flexWrap: 'wrap' }}>
        {METODOS.map((m) => (
          <MetodoCard
            key={m.key}
            metodo={m}
            activo={paymentMethod === m.key}
            motivo={noDisponibles[m.key] || ''}
            onClick={() => setPaymentMethod && setPaymentMethod(m.key)}
          />
        ))}
      </Box>

      {paymentMethod === 'card' ? (
        <>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
            Tu tarjeta solo se registra ahora. El cargo se realiza únicamente cuando el
            proveedor acepta tu reserva.
          </Typography>
          <StripeCard ref={stripeCardRef} />
        </>
      ) : (
        <Box sx={{ bgcolor: 'grey.50', border: 1, borderColor: 'divider', borderRadius: '14px', p: 2.5 }}>
          <Typography variant="body2" fontWeight={700} color="primary.main" sx={{ mb: 0.5 }}>
            {paymentMethod === 'spei' ? 'Pago por transferencia SPEI' : 'Pago en efectivo en OXXO'}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.6 }}>
            {paymentMethod === 'spei'
              ? 'Cuando el proveedor acepte tu reserva te daremos una CLABE y una referencia para hacer la transferencia.'
              : 'Cuando el proveedor acepte tu reserva te daremos una ficha para pagar en cualquier OXXO (OXXO nos avisa del pago hasta el siguiente día hábil).'}
            {' '}Tienes hasta 72 horas, o hasta la hora del servicio si es antes, para pagar: si no llega el pago, la reserva se cancela
            sola. Tu PIN aparece cuando se confirma el pago.
          </Typography>
        </Box>
      )}

      {/* Política de cancelación: el cliente la conoce ANTES de reservar */}
      <Box sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: 'sutil' }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.6 }}>
          <Box component="strong" sx={{ color: 'primary.main' }}>Política de cancelación:</Box> puedes cancelar
          sin costo durante la primera hora después de reservar, o mientras falten más de
          24 horas para el servicio. Después se retiene el 30% como compensación al proveedor.
          Con menos de 3 horas ya no es posible cancelar desde la app.
        </Typography>
      </Box>
    </Box>
  );
};

export default BookingPayment;
